import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import {
	AiConfidenceHealth,
	AiConfidenceQueueResponse,
	AiConfidenceSummary,
	BulkAiConfidenceParams,
	BulkAiConfidenceResult,
	ListAiConfidenceParams,
} from "./types";

const EMPTY_SUMMARY: AiConfidenceSummary = {
	criticalCount: 0,
	highCount: 0,
	mediumCount: 0,
	lowCount: 0,
	okCount: 0,
	checkErrorCount: 0,
	lastCheckedOn: null,
};

export const aiConfidenceService = {
	async listQueue(params: ListAiConfidenceParams): Promise<AiConfidenceQueueResponse> {
		const response = await axios.get(`${getBackendUrl()}/ai-confidence`, {
			params: {
				page: params.page,
				pageSize: params.pageSize,
				...(params.severities?.length ? { severities: params.severities.join(",") } : {}),
				...(params.marketplaceId != null ? { marketplaceId: params.marketplaceId } : {}),
				...(params.triageState ? { triageState: params.triageState } : {}),
				...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
				...(params.sortBy ? { sortBy: params.sortBy } : {}),
				...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
			},
			headers: getAuthHeaders(),
		});

		const payload = response.data ?? {};

		return {
			total: typeof payload.total === "number" ? payload.total : 0,
			page: typeof payload.page === "number" ? payload.page : params.page,
			pageSize: typeof payload.pageSize === "number" ? payload.pageSize : params.pageSize,
			items: Array.isArray(payload.items) ? payload.items : [],
			summary: payload.summary ?? EMPTY_SUMMARY,
			ticketsOnFlaggedMappings:
				typeof payload.ticketsOnFlaggedMappings === "number" ? payload.ticketsOnFlaggedMappings : 0,
		};
	},

	async getHealth(): Promise<AiConfidenceHealth> {
		const response = await axios.get(`${getBackendUrl()}/ai-confidence/health`, { headers: getAuthHeaders() });
		const payload = response.data ?? {};

		return {
			lastCheckedOn: payload.lastCheckedOn ?? null,
			checkErrorsLastDay: typeof payload.checkErrorsLastDay === "number" ? payload.checkErrorsLastDay : 0,
			isHealthy: payload.isHealthy !== false,
		};
	},

	async acknowledge(mappingEventId: number, reason: string): Promise<void> {
		await axios.post(
			`${getBackendUrl()}/ai-confidence/${mappingEventId}/acknowledge`,
			{ reason },
			{ headers: getAuthHeaders() },
		);
	},

	async recheck(mappingEventId: number): Promise<void> {
		await axios.post(`${getBackendUrl()}/ai-confidence/${mappingEventId}/recheck`, {}, { headers: getAuthHeaders() });
	},

	async bulk(params: BulkAiConfidenceParams): Promise<BulkAiConfidenceResult> {
		const response = await axios.post(
			`${getBackendUrl()}/ai-confidence/bulk`,
			{
				action: params.action,
				expectedCount: params.expectedCount,
				...(params.severities?.length ? { severities: params.severities } : {}),
				...(params.marketplaceId != null ? { marketplaceId: params.marketplaceId } : {}),
				...(params.triageState ? { triageState: params.triageState } : {}),
				...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
				...(params.reason ? { reason: params.reason } : {}),
			},
			{ headers: getAuthHeaders() },
		);

		const payload = response.data ?? {};

		return {
			action: params.action,
			matched: typeof payload.matched === "number" ? payload.matched : 0,
			applied: typeof payload.applied === "number" ? payload.applied : 0,
			failed: typeof payload.failed === "number" ? payload.failed : 0,
		};
	},
};
